/** Canvas has no inherited custom properties. SVG colors remain CSS-driven. */
function chartExportColor(color: string): string {
  const styles = getComputedStyle(document.documentElement);
  let resolved = color;
  for (let i = 0; i < 12 && resolved.includes('var('); i++) {
    resolved = resolved.replace(/var\((--[\w-]+)(?:,\s*([^)]*))?\)/g,
      (_, name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback || '#151515');
  }
  return resolved;
}

export interface PdcLegendItem {
  label: string;
  color: string;
}

export interface PdcExportSeries {
  color: string;
  points: Array<{ interval: number; value: number }>;
}

export interface PdcExportPlotLayout {
  chartHeight: number;
  minInterval: number;
  maxInterval: number;
  yMin: number;
  yMax: number;
}

export interface DownloadPdcChartPngOptions {
  svg: SVGSVGElement;
  title?: string;
  footer?: string;
  legend?: PdcLegendItem[];
  series?: PdcExportSeries[];
  plot?: PdcExportPlotLayout;
  unit?: string;
  /** Duration markers in seconds (default: 5s, 1m, 2m, 5m, 10m, 20m, 60m). */
  labelIntervals?: number[];
  fileName: string;
  background?: string;
  scale?: number;
  rtl?: boolean;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const VIEW_W = 640;
const ML = 38;
const MR = 6;
const MT = 2;
const MB = 14;

const FONT = '12px Vazirmatn, Vazir, system-ui, sans-serif';
const TITLE_FONT = '700 14px Vazirmatn, Vazir, system-ui, sans-serif';
const FOOTER_FONT = '11px Vazirmatn, Vazir, system-ui, sans-serif';
const ANNOTATION_FONT = '600 10px Vazirmatn, Vazir, system-ui, sans-serif';

/** Standard PDC duration markers for export labels. */
const DEFAULT_LABEL_INTERVALS = [5, 60, 120, 300, 600, 1200, 3600];

function copySvgPresentationStyles(source: Element, target: Element): void {
  if (!(source instanceof SVGElement) || !(target instanceof SVGElement)) return;
  const cs = getComputedStyle(source);
  const attrs: Record<string, string | undefined> = {
    fill: cs.fill,
    stroke: cs.stroke,
    'stroke-width': cs.strokeWidth,
    opacity: cs.opacity,
    'stop-color': cs.stopColor,
    'stop-opacity': cs.stopOpacity,
    'stroke-dasharray': cs.strokeDasharray,
    'font-family': cs.fontFamily,
    'font-size': cs.fontSize,
    'font-weight': cs.fontWeight,
    'text-anchor': cs.textAnchor,
  };
  for (const [key, value] of Object.entries(attrs)) {
    if (value && value !== 'none' && value !== 'normal') {
      target.setAttribute(key, value);
    }
  }
}

function inlineSvgStyles(svg: SVGSVGElement): SVGSVGElement {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const srcNodes = [svg, ...svg.querySelectorAll('*')];
  const dstNodes = [clone, ...clone.querySelectorAll('*')];
  dstNodes.forEach((dst, index) => copySvgPresentationStyles(srcNodes[index]!, dst));

  const vb = svg.viewBox.baseVal;
  const width = vb.width || Number(svg.getAttribute('width')) || svg.clientWidth;
  const height = vb.height || Number(svg.getAttribute('height')) || svg.clientHeight;
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  if (!clone.getAttribute('viewBox') && width && height) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  return clone;
}

async function svgToImage(svg: SVGSVGElement): Promise<HTMLImageElement> {
  const prepared = inlineSvgStyles(svg);
  const xml = new XMLSerializer().serializeToString(prepared);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('SVG render failed'));
    img.src = dataUrl;
  });
  return img;
}

function logX(interval: number, minI: number, maxI: number, ml: number, cw: number): number {
  const lo = Math.log10(Math.max(minI, 1));
  const hi = Math.log10(Math.max(maxI, minI + 1));
  const v = Math.log10(Math.max(interval, 1));
  return ml + ((v - lo) / (hi - lo || 1)) * cw;
}

function valueToPlotY(value: number, yMin: number, yMax: number, mt: number, ch: number): number {
  const range = yMax - yMin || 1;
  return mt + ch - ((value - yMin) / range) * ch;
}

function interpolateLogValue(
  points: Array<{ interval: number; value: number }>,
  target: number,
): number | null {
  if (!points.length) return null;
  if (target < points[0]!.interval || target > points[points.length - 1]!.interval) return null;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (target >= a.interval && target <= b.interval) {
      if (a.interval === b.interval) return a.value;
      const t =
        (Math.log10(target) - Math.log10(a.interval)) /
        (Math.log10(b.interval) - Math.log10(a.interval));
      return a.value + t * (b.value - a.value);
    }
  }
  return null;
}

function formatAnnotationValue(value: number, unit: string): string {
  if (unit === 'km/h') return value.toFixed(1);
  if (unit === 'W/kg') return value.toFixed(2);
  return String(Math.round(value));
}

interface LegendRow {
  items: PdcLegendItem[];
  width: number;
}

/** Pack legend items in array order; wrap when row exceeds maxWidth. */
function layoutLegendRows(
  ctx: CanvasRenderingContext2D,
  items: PdcLegendItem[],
  maxWidth: number,
): LegendRow[] {
  const swatch = 10;
  const gap = 18;
  const itemGap = 20;
  const rows: LegendRow[] = [];
  let current: PdcLegendItem[] = [];
  let currentW = 0;

  for (const item of items) {
    ctx.font = FONT;
    const textW = ctx.measureText(item.label).width;
    const itemW = swatch + gap + textW + itemGap;
    if (current.length > 0 && currentW + itemW > maxWidth) {
      rows.push({ items: current, width: currentW - itemGap });
      current = [];
      currentW = 0;
    }
    current.push(item);
    currentW += itemW;
  }
  if (current.length) {
    rows.push({ items: current, width: currentW - itemGap });
  }
  return rows;
}

function drawHorizontalLegend(
  ctx: CanvasRenderingContext2D,
  rows: LegendRow[],
  topY: number,
  totalWidth: number,
  pad: number,
  rtl: boolean,
): number {
  const rowH = 22;
  const swatchR = 4;
  const swatchGap = 8;
  const itemGap = 20;

  rows.forEach((row, rowIndex) => {
    const rowY = topY + rowIndex * rowH;
    ctx.font = FONT;

    if (rtl) {
      // First item in row sits on the right; subsequent items flow left.
      let x = totalWidth - pad;
      for (const item of row.items) {
        const textW = ctx.measureText(item.label).width;
        const itemW = swatchR * 2 + swatchGap + textW;
        x -= itemW;
        const swatchX = x + itemW - swatchR;
        ctx.fillStyle = chartExportColor(item.color);
        ctx.beginPath();
        ctx.arc(swatchX, rowY + 11, swatchR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = chartExportColor('var(--palette-text)');
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.label, swatchX - swatchGap, rowY + 11);
        x -= itemGap;
      }
    } else {
      let x = pad;
      for (const item of row.items) {
        const textW = ctx.measureText(item.label).width;
        const itemW = swatchR * 2 + swatchGap + textW;
        const swatchX = x + swatchR;
        ctx.fillStyle = chartExportColor(item.color);
        ctx.beginPath();
        ctx.arc(swatchX, rowY + 11, swatchR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = chartExportColor('var(--palette-text)');
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.label, swatchX + swatchGap, rowY + 11);
        x += itemW + itemGap;
      }
    }
  });

  return rows.length * rowH;
}

function drawSeriesAnnotations(
  ctx: CanvasRenderingContext2D,
  plotX: number,
  plotY: number,
  plotW: number,
  plotH: number,
  plot: PdcExportPlotLayout,
  series: PdcExportSeries[],
  unit: string,
  labelIntervals: number[],
): void {
  const cw = VIEW_W - ML - MR;
  const ch = plot.chartHeight - MT - MB;
  const scaleX = plotW / VIEW_W;
  const scaleY = plotH / plot.chartHeight;

  const activeIntervals = labelIntervals.filter(
    t => t >= plot.minInterval && t <= plot.maxInterval,
  );

  for (let si = 0; si < series.length; si++) {
    const s = series[si]!;
    for (const target of activeIntervals) {
      const value = interpolateLogValue(s.points, target);
      if (value == null) continue;

      const xSvg = logX(target, plot.minInterval, plot.maxInterval, ML, cw);
      const ySvg = valueToPlotY(value, plot.yMin, plot.yMax, MT, ch);
      const px = plotX + xSvg * scaleX;
      const py = plotY + ySvg * scaleY;

      ctx.fillStyle = chartExportColor(s.color);
      ctx.strokeStyle = chartExportColor('var(--palette-surface)');
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      const label = formatAnnotationValue(value, unit);
      const stackOffset = (si - (series.length - 1) / 2) * 12;
      const labelY = py - 8 + stackOffset;

      ctx.font = ANNOTATION_FONT;
      ctx.fillStyle = chartExportColor(s.color);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(label, px, labelY);
    }
  }
}

async function triggerCanvasDownload(canvas: HTMLCanvasElement, fileName: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('PNG export failed'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
      anchor.click();
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
}

/** Renders a PDC SVG plot (+ title, value labels, horizontal legend, footer) to PNG. */
export async function downloadPdcChartPng(options: DownloadPdcChartPngOptions): Promise<void> {
  await document.fonts.ready;

  const scale = options.scale ?? 2;
  const bg = chartExportColor(options.background ?? 'var(--palette-surface)');
  const pad = 16;
  const titleH = options.title ? 26 : 0;
  const titleGap = options.title ? 10 : 0;
  const footerGap = 10;
  const footerH = options.footer ? 18 + footerGap : 0;
  const legend = options.legend ?? [];
  const rtl = options.rtl ?? false;

  const img = await svgToImage(options.svg);
  const plotW = img.naturalWidth || img.width;
  const plotH = img.naturalHeight || img.height;

  const scratch = document.createElement('canvas');
  const measureCtx = scratch.getContext('2d');
  if (!measureCtx) throw new Error('Canvas unavailable');
  measureCtx.font = FONT;
  const legendRows = legend.length
    ? layoutLegendRows(measureCtx, legend, plotW)
    : [];
  const legendH = legendRows.length ? legendRows.length * 22 + 12 : 0;

  const totalW = plotW + pad * 2;
  const totalH = pad + titleH + titleGap + plotH + legendH + footerH + pad;

  const canvas = document.createElement('canvas');
  canvas.width = totalW * scale;
  canvas.height = totalH * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');

  ctx.scale(scale, scale);
  ctx.fillStyle = chartExportColor('var(--palette-surface)');
  ctx.fillRect(0, 0, totalW, totalH);

  let y = pad;
  if (options.title) {
    ctx.font = TITLE_FONT;
    ctx.fillStyle = chartExportColor('var(--palette-text)');
    ctx.textAlign = rtl ? 'right' : 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(options.title, rtl ? totalW - pad : pad, y);
    y += titleH + titleGap;
  }

  const plotX = pad;
  const plotY = y;
  ctx.fillStyle = bg;
  ctx.fillRect(plotX, plotY, plotW, plotH);
  ctx.drawImage(img, plotX, plotY, plotW, plotH);

  if (options.series?.length && options.plot) {
    drawSeriesAnnotations(
      ctx,
      plotX,
      plotY,
      plotW,
      plotH,
      options.plot,
      options.series,
      options.unit ?? 'W',
      options.labelIntervals ?? DEFAULT_LABEL_INTERVALS,
    );
  }

  y += plotH + 8;

  if (legendRows.length) {
    drawHorizontalLegend(ctx, legendRows, y, totalW, pad, rtl);
    y += legendRows.length * 22 + 4;
  }

  if (options.footer) {
    y += footerGap;
    ctx.font = FOOTER_FONT;
    ctx.fillStyle = chartExportColor('var(--palette-text-muted)');
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(options.footer, totalW / 2, y);
  }

  await triggerCanvasDownload(canvas, options.fileName);
}
